import {
  AgoraJoinChannelAction,
  AgoraLeaveChannelAction,
  VideoCallAction,
  AgoraPublishLocalStream,
  AgoraUnPublishLocalStream,
  AgoraReJoinChannelAction,
} from 'components/features/videoCalls/types/actions'
import { mergeMap, catchError, takeUntil, take, map } from 'rxjs/operators'
import { of, merge, EMPTY } from 'rxjs'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { EpicRefined } from 'interfaces/middleware'
import { fromClientEvents, createAgoraClient, agoraClientLeave } from 'components/features/videoCalls/agoraHelpers'
import { AdvisorVideoCallActions, FinishMeetingAction } from 'components/features/coaching/advisorVideoCall/types/actions'
import { mergeMapAsync } from 'rxOperators/mergeMapAsync'
import { agoraInstances } from 'components/features/videoCalls/agoraInstances'
import { filterAgoraClientEvent } from 'components/features/videoCalls/helpers'
import { byType } from 'rxOperators/byType'

export const joinChannelEpic: EpicRefined<VideoCallAction | AdvisorVideoCallActions> = (action$) => action$.pipe(
  filterRequestOfType(AgoraJoinChannelAction.type, 'request'),
  mergeMap((action) => of(null).pipe(
    mergeMapAsync(async () => {
      const { token, channel, uid } = action.payload.params
      const { client, localStreamId } = await createAgoraClient(token, channel, uid)
      client.enableAudioVolumeIndicator()
      const clientId = agoraInstances.addClient(client)

      return {
        client,
        clientId,
        localStreamId,
      }
    }),
    mergeMap(({ client, clientId, localStreamId }) => {
      const leave$ = action$.pipe(
        filterRequestOfType(AgoraLeaveChannelAction.type, 'request'),
      )
      const clientEvents$ = fromClientEvents(client).pipe(
        takeUntil(leave$),
      )

      return merge(
        clientEvents$,
        of(new AgoraPublishLocalStream()),
        of(action.success({
          localStreamId,
          clientId,
        })),
      )
    }),
    catchError(() => {
      return merge(
        of(action.fail()),
        of(new AgoraLeaveChannelAction()),
      )
    }),
  )),
)

export const leaveChannelEpic: EpicRefined<VideoCallAction> = (action$, store) => action$.pipe(
  filterRequestOfType(AgoraLeaveChannelAction.type, 'request'),
  mergeMap((action) => of(null).pipe(
    mergeMapAsync(async () => {
      const { clientId } = store.getState().app.videoCall.client
      const client = agoraInstances.clients.get(clientId as any)
      if (!client) {
        throw new Error()
      }

      await agoraClientLeave(client)
      agoraInstances.clients.delete(clientId as any)
    }),
    mergeMap(() => {
      const unpublishDone$ = merge(
        action$.pipe(
          filterRequestOfType(AgoraUnPublishLocalStream.type, 'success')
        ),
        action$.pipe(
          filterRequestOfType(AgoraUnPublishLocalStream.type, 'fail')
        ),
      )
      return merge(
        unpublishDone$.pipe(
          take(1),
          map(() => action.success())
        ),
        of(new AgoraUnPublishLocalStream()),
      )
    }),
    catchError(() => of(action.fail())),
  )),
)

export const leaveChannelSuccessEpic: EpicRefined<VideoCallAction> = (action$) => action$.pipe(
  filterRequestOfType(AgoraLeaveChannelAction.type, 'success'),
  mergeMap(() => {
    agoraInstances.streams.clear()
    return EMPTY
  })
)

export const tokenPrivilegeWillExpireEpic: EpicRefined<VideoCallAction | AdvisorVideoCallActions> = (action$) => action$.pipe(
  filterAgoraClientEvent('onTokenPrivilegeWillExpire'),
  mergeMap(() => of(new FinishMeetingAction()))
)

// AgoraReJoinChannelAction
export const reJoinChannelActionEpic: EpicRefined<VideoCallAction> = (action$, store) => action$.pipe(
  byType(AgoraReJoinChannelAction.type),
  mergeMap(() => {
    const state = store.getState()
    const { client } = state.app.videoCall
    const connection = state.data.advisors.videoCallDetails.details?.video?.connection ?? null

    if (client.state !== 'active' || !connection) {
      return EMPTY
    }

    const leaveDone$ = merge(
      action$.pipe(
        filterRequestOfType(AgoraLeaveChannelAction.type, 'success'),
      ),
      action$.pipe(
        filterRequestOfType(AgoraLeaveChannelAction.type, 'fail'),
      )
    ).pipe(
      take(1)
    )

    const join$ = leaveDone$.pipe(
      map(() => new AgoraJoinChannelAction(connection)),
    )

    return merge(
      join$,
      of(new AgoraLeaveChannelAction()),
    )
  }),
)
